import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

const Chart = ({ 
  type = 'line', 
  data = [], 
  width = '100%', 
  height = 300,
  colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'],
  dataKeys = [],
  options = {}
}) => {
  const renderChart = () => {
    switch (type) {
      case 'line':
        // If dataKeys are provided, render multiple lines
        if (dataKeys && dataKeys.length > 0) {
          return (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 0, 0, 0.05)" />
              <XAxis 
                dataKey="name" 
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => {
                  // Check if data contains percentage values (between 0-100)
                  const isPercentage = data.length > 0 && data.some(item => item.value <= 100 && item.value >= 0);
                  if (isPercentage) {
                    return `${value}%`;
                  }
                  return value >= 1000 ? `${(value / 1000).toFixed(1)}K` : value;
                }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  color: '#111827'
                }}
                labelStyle={{ color: '#111827', fontWeight: 'bold' }}
                formatter={(value, name) => {
                  if (name.includes('avgTime') || name.includes('Time')) {
                    return [`${value}ms`, name];
                  }
                  if (name.includes('Health') || name.includes('%')) {
                    return [`${value.toFixed(2)}%`, name];
                  }
                  return [value.toLocaleString(), name];
                }}
              />
              {options.legend !== false && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
              {dataKeys.map((key, index) => (
                <Line 
                  key={key.dataKey}
                  type="monotone" 
                  dataKey={key.dataKey} 
                  name={key.name}
                  stroke={colors[index % colors.length]} 
                  strokeWidth={3}
                  dot={{ r: 4, fill: '#fff', strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
              ))}
            </LineChart>
          );
        }
        
        // Default single line
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke={colors[0]} strokeWidth={2} />
          </LineChart>
        );
      
      case 'bar':
        // If dataKeys are provided, render multiple bars
        if (dataKeys && dataKeys.length > 0) {
          return (
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 0, 0, 0.05)" />
              <XAxis 
                dataKey="name" 
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              {options.legend !== false && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
              {dataKeys.map((key, index) => (
                <Bar 
                  key={key.dataKey}
                  dataKey={key.dataKey} 
                  name={key.name}
                  fill={colors[index % colors.length]} 
                />
              ))}
            </BarChart>
          );
        }
        
        // Default single bar
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="value" fill={colors[0]} />
          </BarChart>
        );
      
      case 'pie':
        return (
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value, name) => [`${value}`, name]}
            />
          </PieChart>
        );
      
      case 'area':
        // If dataKeys are provided, render multiple areas
        if (dataKeys && dataKeys.length > 0) {
          return (
            <AreaChart data={data}>
              <defs>
                {dataKeys.map((key, index) => (
                  <linearGradient key={key.dataKey} id={`color${index}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={colors[index % colors.length]} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={colors[index % colors.length]} stopOpacity={0.1}/>
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 0, 0, 0.05)" />
              <XAxis 
                dataKey="name" 
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              {options.legend !== false && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
              {dataKeys.map((key, index) => (
                <Area 
                  key={key.dataKey}
                  type="monotone" 
                  dataKey={key.dataKey} 
                  name={key.name}
                  stroke={colors[index % colors.length]} 
                  fill={`url(#color${index})`}
                  strokeWidth={2}
                />
              ))}
            </AreaChart>
          );
        }
        
        // Default single area
        return (
          <AreaChart data={data}>
            <defs>
              <linearGradient id="colorArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors[0]} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={colors[0]} stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Area type="monotone" dataKey="value" stroke={colors[0]} fill="url(#colorArea)" />
          </AreaChart>
        );
      
      case 'donut':
        return (
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
              outerRadius={100}
              innerRadius={60}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value, name) => [`${value}`, name]}
            />
          </PieChart>
        );
      
      default:
        return null;
    }
  };

  return (
    <div style={{ width, height }}>
      <ResponsiveContainer width="100%" height="100%">
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
};

export default Chart;
